import { WarningAmberRounded } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { AddendumStatus, InspectionStatus } from '@prisma/client';
import useIsMobile from '../../../hooks/useIsMobile';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { Status } from '../../../types/Status';
import transactionsService from '../../../api/services/transactions';
import {
  fetchTransactions,
  transactionsWithUserAndOffer,
} from '../../../store/slices/transactions';
import { useAppDispatch } from '../../../store/store';
import { fetchStatus } from '../../../store/slices/status';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import NiceModal from '@ebay/nice-modal-react';
// eslint-disable-next-line max-len
import RepairRequestSubmitModal from '../../../components/RepairRequestSubmitModal/RepairRequestSubmitModal';
import { isTransactionBuyer } from '../../../utils/transactionParties';

const MARK_COMPLETE_HINT =
  'If there are no repairs needed, you may mark inspections complete.';
const UPLOAD_PROMPT =
  'Please submit any repair requests before inspections are due. ' +
  MARK_COMPLETE_HINT;
const REVIEW_PROMPT =
  'Review your inspection summary and choose what to ask for before ' +
  `inspections are due. ${MARK_COMPLETE_HINT}`;

interface Props {
  transaction: transactionsWithUserAndOffer;
  status: Status;
}

const RepairRequest = ({ transaction, status }: Props) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { currentUser } = useAuth();
  const isPBuyer = currentUser?.type === 'PBUYER';
  const isAdmin = currentUser?.type === 'ADMIN';
  // Regular buyers used to be locked out of repair requests entirely
  // (issue #222); they can now submit their own, through the inspection summary
  // rather than the document-upload modal. PBUYER and admin accounts keep the
  // modal. Ownership is resolved by the same helper the dashboard uses, so the
  // CTA can never send a buyer to a page that will not render the surface.
  const isBuyer = isTransactionBuyer(transaction, currentUser?.id);
  const usesUploadModal = isPBuyer || isAdmin;
  const canSubmitRepairs = usesUploadModal || isBuyer;
  const addendumStatus = status.inspection.addendum;
  const [confirmOpen, setConfirmOpen] = useState(false);

  const openRepairRequest = () => {
    if (usesUploadModal) {
      NiceModal.show(RepairRequestSubmitModal, { transaction });
      return;
    }
    navigate(`/transactions/${transaction.id}?section=inspection`);
  };

  const markInspectionComplete = async () => {
    await transactionsService.patch(transaction.id, {
      inspection: InspectionStatus.COMPLETED,
    });
    dispatch(fetchTransactions());
    dispatch(fetchStatus(transaction.listingId));
    setConfirmOpen(false);
  };

  if (
    canSubmitRepairs &&
    (addendumStatus === null || addendumStatus === AddendumStatus.COUNTERED) &&
    status.inspection.completed === false &&
    // Before the inspection has happened there is no report to review and
    // nothing to ask for; marking it complete then would close the step and
    // the repair window before the buyer has seen anything.
    status.inspection.occurred
  ) {
    return (
      <Alert severity="info">
        <AlertTitle>Repair Requests</AlertTitle>
        <Grid2
          container
          direction={isMobile ? 'column' : 'row'}
          spacing={2}
          alignItems={isMobile ? 'flex-start' : 'center'}
          justifyContent="space-between"
          width="100%"
        >
          <Grid2 xs={12} md={8}>
            <Typography>
              {usesUploadModal ? UPLOAD_PROMPT : REVIEW_PROMPT}
            </Typography>
          </Grid2>
          <Grid2 xs={12} md={4}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="flex-end"
              width="100%"
            >
              <Button
                variant="contained"
                color="primary"
                onClick={openRepairRequest}
              >
                {usesUploadModal ? 'Submit Requests' : 'Review Inspection'}
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => setConfirmOpen(true)}
              >
                Mark Complete
              </Button>
            </Stack>
          </Grid2>
        </Grid2>
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>Mark inspections complete?</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This tells us you are not asking the seller for any repairs and
              moves your purchase past the inspection step.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                markInspectionComplete();
              }}
            >
              Mark Complete
            </Button>
          </DialogActions>
        </Dialog>
      </Alert>
    );
  }

  if (addendumStatus !== null) {
    let buyerText;
    if (addendumStatus === AddendumStatus.COUNTERED) {
      buyerText =
        // eslint-disable-next-line max-len
        'You have countered the repair requests. The buyers may now accept the counter or submit new requests.';
    } else {
      buyerText = canSubmitRepairs
        ? 'You have requested repairs. You can view the details here.'
        : // eslint-disable-next-line max-len
          'The buyer has requested repairs. Please review the repairs carefully and respond accordingly.';
    }
    return (
      <Alert
        severity="warning"
        icon={<WarningAmberRounded />}
        sx={{
          '& .MuiAlert-message': {
            width: '100%',
          },
        }}
      >
        <AlertTitle>Repairs Requested</AlertTitle>
        <Grid2
          container
          direction={isMobile ? 'column' : 'row'}
          rowGap={2}
          alignItems={isMobile ? 'flex-start' : 'center'}
          justifyContent="space-between"
        >
          <Grid2 xs={12} md={8}>
            <Typography>{buyerText}</Typography>
          </Grid2>
          <Grid2 xs={12} md={4} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              fullWidth={isMobile}
              onClick={() => {
                navigate(`/transactions/${transaction?.id}?section=inspection`);
              }}
            >
              View Repairs
            </Button>
          </Grid2>
        </Grid2>
      </Alert>
    );
  }

  return null;
};

export default RepairRequest;
